/* A Card showing details of a fish species, including its image, name, and a button to log consumption. */
import { useState } from "react";
import { FishSpecies } from "../data/fish_db";
import { getFishNutrients } from "../data/fishWithNutrients";
import { useLang } from "../contexts/LangContext";
import { calculatePercentDRI } from "../data/nutrientReferenceValues";

const FishDetailsCard = ({ fish }: { fish: FishSpecies }) => {
  const { lang } = useLang();
  const [showAllNutrients, setShowAllNutrients] = useState(false);

  // Get nutrients from CSV (or fallback)
  const nutrients = getFishNutrients(fish.id);

  // Calculate top nutrients dynamically (exclude sodium as high sodium is not beneficial)
  const topNutrients = nutrients
    ? Object.entries(nutrients)
        .filter(([key]) => key !== "sodium") // Exclude sodium
        .map(([key, data]) => ({
          key,
          value: data.value,
          percentOfReference: calculatePercentDRI(key, data.value) || 0,
        }))
        .sort((a, b) => b.percentOfReference - a.percentOfReference)
        .slice(0, 7)
        .map((item) => item.key)
    : [];

  const nutrientKeyMap: Record<string, string> = {
    protein: "Protein",
    fat: "Fat",
    energy: "Energy",
    calcium: "Calcium",
    iron: "Iron",
    zinc: "Zinc",
    vitaminA: "Vitamin A",
    vitaminD: "Vitamin D",
    vitaminE: "Vitamin E",
    omega3: "Omega-3",
    iodine: "Iodine",
    sodium: "Sodium",
    potassium: "Potassium",
    magnesium: "Magnesium",
    phosphorus: "Phosphorus",
    copper: "Copper",
    manganese: "Manganese",
    sulphur: "Sulphur",
    selenium: "Selenium",
    thiamin: "Thiamin (B1)",
    riboflavin: "Riboflavin (B2)",
    niacin: "Niacin (B3)",
    vitaminB6: "Vitamin B6",
    folate: "Folate (B9)",
    vitaminB12: "Vitamin B12",
  };

  return (
    <div className="flex flex-col items-center justify-center bg-red rounded-xl shadow-lg overflow-hidden select-none">
      <img
        src={fish.image_url}
        alt={fish.english_name}
        className="w-full aspect-[5/3] object-cover bg-white"
      />
      <div className="w-full bg-white bg-opacity-75 backdrop-blur-md text-center py-4">
        <h2 className="text-2xl font-semibold text-gray-800 px-4">
          {fish.english_name} ({fish.local_name})
        </h2>
        {fish.scientific_name && (
          <p className="text-sm text-gray-600 italic font-serif px-4">{fish.scientific_name}</p>
        )}
        {fish.aliases && fish.aliases.length > 0 && (
          <p className="text-sm text-gray-700 px-4 mt-1">
            Also known as: <span className="font-medium text-blue-800">{fish.aliases.join(", ")}</span>
          </p>
        )}
        
        {nutrients && (
          <div className="mt-4 max-h-64 overflow-y-auto px-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-2 text-gray-700 font-semibold">Nutrient</th>
                  <th className="text-right py-2 px-2 text-gray-700 font-semibold">Per 100g</th>
                  <th className="text-right py-2 px-2 text-gray-700 font-semibold">% DRI</th>
                </tr>
              </thead>
              <tbody>
                {/* Top nutrients - dynamically selected */}
                {Object.entries(nutrients)
                  .filter(([key]) => topNutrients.includes(key))
                  .sort((a, b) => topNutrients.indexOf(a[0]) - topNutrients.indexOf(b[0]))
                  .map(([nutrient, data]) => {
                    const percentDRI = calculatePercentDRI(nutrient, data.value);
                    return (
                      <tr key={nutrient} className="border-b border-gray-200">
                        <td className="py-2 px-2 text-left text-gray-800">{nutrientKeyMap[nutrient] || nutrient}</td>
                        <td className="py-2 px-2 text-right text-sky-600 font-medium">
                          {data.value}{data.unit}
                        </td>
                        <td className="py-2 px-2 text-right text-gray-600 text-xs">
                          {percentDRI !== null ? `${percentDRI.toFixed(0)}%` : '-'}
                        </td>
                      </tr>
                    );
                  })}
                
                {/* Other nutrients - collapsible */}
                {Object.entries(nutrients).filter(([key]) => !topNutrients.includes(key)).length > 0 && (
                  <>
                    <tr>
                      <td colSpan={3} className="py-2 px-2">
                        <button
                          onClick={() => setShowAllNutrients(!showAllNutrients)}
                          className="text-xs text-gray-500 hover:text-gray-700 underline w-full text-center"
                        >
                          {showAllNutrients ? "Hide" : "Show"} {Object.entries(nutrients).filter(([key]) => !topNutrients.includes(key)).length} more nutrients
                        </button>
                      </td>
                    </tr>
                    
                    {showAllNutrients && Object.entries(nutrients)
                      .filter(([key]) => !topNutrients.includes(key))
                      .map(([nutrient, data]) => {
                        const percentDRI = calculatePercentDRI(nutrient, data.value);
                        return (
                          <tr key={nutrient} className="border-b border-gray-200">
                            <td className="py-2 px-2 text-left text-gray-700 text-xs">{nutrientKeyMap[nutrient] || nutrient}</td>
                            <td className="py-2 px-2 text-right text-gray-600 text-xs font-medium">
                              {data.value}{data.unit}
                            </td>
                            <td className="py-2 px-2 text-right text-gray-500 text-xs">
                              {percentDRI !== null ? `${percentDRI.toFixed(0)}%` : '-'}
                            </td>
                          </tr>
                        );
                      })}
                  </>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FishDetailsCard;
